import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ArrowLeft, Users, TrendingUp, AlertCircle, Lock, Download, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://metax-backend.onrender.com';
const API = `${BACKEND_URL}/api`;

const riskZone = (bmi) => {
  const v = Number(bmi);
  if (!v) return null;
  if (v >= 32.5) return 'red';
  if (v >= 27.5) return 'yellow';
  return 'green';
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [adminKey, setAdminKey] = useState(sessionStorage.getItem('metax_admin_key') || '');
  const [authed, setAuthed] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [verifying, setVerifying] = useState(false);

  const [patients, setPatients] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async (key) => {
    setLoading(true);
    const headers = { 'X-Admin-Key': key };
    try {
      const [patientsRes, leadsRes] = await Promise.all([
        axios.get(`${API}/patients`, { headers }),
        axios.get(`${API}/leads`, { headers }),
      ]);
      setPatients(Array.isArray(patientsRes.data) ? patientsRes.data : []);
      setLeads(Array.isArray(leadsRes.data) ? leadsRes.data : []);
      return true;
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Invalid admin password');
      } else if (error.response?.status === 503) {
        toast.error('Admin access not configured on the server (set ADMIN_PASSWORD)');
      } else {
        toast.error('Failed to load data — server may be waking up, try again');
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-login if a key is already in this browser session
  useEffect(() => {
    if (adminKey && !authed) {
      fetchData(adminKey).then((ok) => {
        if (ok) setAuthed(true);
        else sessionStorage.removeItem('metax_admin_key');
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!passwordInput) return;
    setVerifying(true);
    const ok = await fetchData(passwordInput);
    setVerifying(false);
    if (ok) {
      sessionStorage.setItem('metax_admin_key', passwordInput);
      setAdminKey(passwordInput);
      setAuthed(true);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('metax_admin_key');
    setAdminKey('');
    setAuthed(false);
    setPatients([]);
    setLeads([]);
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/patients/export`, {
        headers: { 'X-Admin-Key': adminKey },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'metax_patients.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const stats = {
    total: patients.length,
    highRisk: patients.filter(p => riskZone(p.BMI) === 'red').length,
    mediumRisk: patients.filter(p => riskZone(p.BMI) === 'yellow').length,
  };

  const comorbidities = (p) => {
    const items = [];
    if (String(p.Has_Diabetes) === 'True') items.push('Type 2 Diabetes');
    if (String(p.Has_HTN) === 'True') items.push('Hypertension');
    if (String(p.Has_OSA) === 'True') items.push('Sleep Apnea');
    if (String(p.Has_GERD) === 'True') items.push('GERD');
    if (String(p.Has_OA) === 'True') items.push('Osteoarthritis');
    return items;
  };

  // ── LOGIN GATE ──
  if (!authed) {
    return (
      <div className="min-h-screen bg-warm-sand flex items-center justify-center px-4">
        <form onSubmit={handleLogin} className="glass-card rounded-2xl p-10 w-full max-w-md" data-testid="admin-login-form">
          <div className="w-14 h-14 rounded-full bg-soft-blue flex items-center justify-center mx-auto mb-6">
            <Lock className="w-7 h-7 text-slate-navy" />
          </div>
          <h1 className="text-2xl font-manrope font-semibold text-slate-navy text-center mb-2">Admin Access</h1>
          <p className="text-sm text-slate-600 text-center mb-8">This area contains patient data and is restricted to authorized team members.</p>
          <input
            type="password"
            data-testid="admin-password-input"
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
            placeholder="Admin password"
            className="w-full p-3 bg-white rounded-lg border border-slate-300 mb-4"
            autoFocus
          />
          <button
            type="submit"
            data-testid="admin-login-button"
            disabled={verifying || !passwordInput}
            className="w-full py-3 bg-slate-900 text-white rounded-xl font-bold uppercase tracking-widest text-xs hover:bg-slate-800 transition disabled:opacity-40"
          >
            {verifying ? 'Verifying...' : 'Unlock Dashboard'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="w-full mt-4 text-xs text-slate-500 hover:text-slate-900"
          >
            ← Back to Home
          </button>
        </form>
      </div>
    );
  }

  // ── DASHBOARD ──
  return (
    <div className="min-h-screen bg-warm-sand">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Header */}
        <div className="flex items-start justify-between mb-12 flex-wrap gap-4">
          <div>
            <button
              data-testid="back-to-home-button"
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-slate-900 hover:text-slate-navy transition-colors mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </button>
            <h1 className="text-4xl md:text-5xl font-manrope font-light tracking-tight text-slate-navy">
              Patient Dashboard
            </h1>
            <p className="text-slate-900 mt-2 text-lg">Assessments and consultation requests</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleExport}
              data-testid="export-button"
              className="flex items-center gap-2 px-5 py-3 bg-slate-900 text-white rounded-xl font-bold uppercase tracking-wider text-xs hover:bg-slate-800 transition"
            >
              <Download className="w-4 h-4" /> Export Excel
            </button>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="flex items-center gap-2 px-5 py-3 bg-white border border-slate-300 text-slate-700 rounded-xl font-bold uppercase tracking-wider text-xs hover:bg-slate-100 transition"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="glass-card rounded-2xl p-6" data-testid="total-leads-stat">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-soft-blue flex items-center justify-center">
                <Users className="w-6 h-6 text-slate-navy" />
              </div>
              <div>
                <p className="text-sm text-slate-900">Total Assessments</p>
                <p className="text-3xl font-semibold text-slate-navy">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="glass-card rounded-2xl p-6" data-testid="high-risk-stat">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full" style={{backgroundColor: '#FEE2E2'}}>
                <AlertCircle className="w-6 h-6 m-3" style={{color: '#991B1B'}} />
              </div>
              <div>
                <p className="text-sm text-slate-900">High Risk (BMI ≥ 32.5)</p>
                <p className="text-3xl font-semibold text-slate-navy">{stats.highRisk}</p>
              </div>
            </div>
          </div>

          <div className="glass-card rounded-2xl p-6" data-testid="medium-risk-stat">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full" style={{backgroundColor: '#FEF3C7'}}>
                <TrendingUp className="w-6 h-6 m-3" style={{color: '#92400E'}} />
              </div>
              <div>
                <p className="text-sm text-slate-900">Medium Risk (BMI 27.5–32.4)</p>
                <p className="text-3xl font-semibold text-slate-navy">{stats.mediumRisk}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Patients Table */}
        <div className="glass-card rounded-2xl p-8 mb-12" data-testid="patients-table-container">
          <h2 className="text-2xl font-manrope font-normal text-slate-navy mb-6">Digital Twin Assessments</h2>
          {loading ? (
            <div className="text-center py-12 text-slate-900">Loading...</div>
          ) : patients.length === 0 ? (
            <div className="text-center py-12 text-slate-900">No assessments recorded yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>City</th>
                    <th>Age</th>
                    <th>BMI</th>
                    <th>Risk</th>
                    <th>Procedure</th>
                    <th>Comorbidities</th>
                  </tr>
                </thead>
                <tbody>
                  {patients.map((p, i) => {
                    const zone = riskZone(p.BMI);
                    const morbid = comorbidities(p);
                    return (
                      <tr key={i} data-testid={`patient-row-${i}`}>
                        <td className="text-sm text-slate-900 whitespace-nowrap">{String(p.Timestamp || '').split(' ')[0]}</td>
                        <td className="font-medium text-slate-navy">{p.Name || '—'}</td>
                        <td>{p.Phone || '—'}</td>
                        <td>{p.City || '—'}</td>
                        <td>{p.Age}</td>
                        <td className="font-semibold">{p.BMI}</td>
                        <td>
                          {zone && (
                            <span className={`risk-badge ${zone}`}>
                              {zone.toUpperCase()}
                            </span>
                          )}
                        </td>
                        <td>{p.Procedure}</td>
                        <td>
                          <div className="flex flex-col gap-1 text-sm">
                            {morbid.map(m => <span key={m}>• {m}</span>)}
                            {morbid.length === 0 && <span className="text-slate-400">None</span>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Leads Table */}
        <div className="glass-card rounded-2xl p-8" data-testid="leads-table-container">
          <h2 className="text-2xl font-manrope font-normal text-slate-navy mb-6">Consultation Requests</h2>
          {loading ? (
            <div className="text-center py-12 text-slate-900">Loading...</div>
          ) : leads.length === 0 ? (
            <div className="text-center py-12 text-slate-900">No consultation requests yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Preferred Callback Time</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead, i) => (
                    <tr key={i} data-testid={`lead-row-${i}`}>
                      <td className="text-sm text-slate-900 whitespace-nowrap">{String(lead.Timestamp || '').split(' ')[0]}</td>
                      <td className="font-medium text-slate-navy">{lead.Name}</td>
                      <td>{lead.Phone}</td>
                      <td>{lead.Preferred_Callback_Time}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
